import { GET_REVIEWS_QUERY, ReviewData } from "@/gql/review";
import { getApolloData } from "@/utils/apollo/get-data";
import { logger } from "@/utils/logger";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React from "react";
import { TestimonialsClient } from "./client";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

export interface Testimonial {
	id: string;
	name: string;
	timeAgo: string;
	rating: number;
	review: string;
	summary: string;
	avatar?: string;
}

// Helper function to transform review data to testimonial format
export const transformReviewToTestimonial = (review: ReviewData, index: number): Testimonial => {
	// Calculate time ago from createdAt with error handling
	let timeAgo = "Recent";
	if (review.createdAt) {
		try {
			const date = dayjs(review.createdAt);
			if (date.isValid()) {
				timeAgo = date.fromNow();
			}
		} catch (_) {}
	}

	return {
		id: `review-${index}`,
		name: review.reviewer?.displayName || "Anonymous User",
		timeAgo,
		rating: review.rating,
		review: review.review,
		summary: review.summary || "",
		avatar: review.reviewer?.profilePhotoUrl,
	};
};

export const Testimonials: React.FC = async () => {
	// Fetch reviews from Apollo server on the server side
	const { data, error } = await getApolloData<{
		reviews: {
			get: {
				count: number;
				data: Testimonial[]; // Review data structure
			};
		};
	}>({
		query: GET_REVIEWS_QUERY,
		variables: {
			params: {
				get: {
					limit: 10, // Get 10 reviews for the carousel
				},
			},
		},
		fetchPolicy: "cache-first",
	});

	if (error) {
		logger.error("Error loading testimonials:", error);
	}

	// Transform Apollo data to testimonials format
	let testimonials: Testimonial[] = [];
	if (data?.reviews?.get) {
		testimonials = data.reviews.get.data.map(transformReviewToTestimonial);
	} else {
		logger.error("No reviews data found:", {
			hasData: !!data,
			hasReviews: !!data?.reviews,
		});
	}

	if (!testimonials.length) {
		return null;
	}

	return (
		<section className="py-12 md:py-24 bg-muted/20 max-w-7xl mx-auto" aria-labelledby="testimonials-heading">
			<div className="container px-4 mx-auto">
				<TestimonialsClient testimonials={testimonials} />
			</div>
		</section>
	);
};
